import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Params } from '@angular/router';
import { Store } from '@ngrx/store';
import { defaultIfEmpty, EMPTY, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, take, tap } from 'rxjs/operators';

import { CustomLanguage } from '@ninety/platform/models/language/custom-language';
import { DatadogLoggerFactory } from '@ninety/platform/services/vendor/datadog';
import {
  EntitlementsService,
  FeatureFlagKeys,
  NotificationActions,
  selectEntitlements,
  selectFeatureFlag,
  selectLanguage,
  SpinnerActions,
} from '@ninety/platform/state';
import { environment } from '@ninety/ui/web/environments';

import { NativeIntegrationDisconnectHandler } from './native-integration-disconnect-handler';
import { NativeIntegration, NativeIntegrationsResponse } from './native.models';
import { OAUTH_POPUP_MESSAGE_SOURCE, OAuthPopupMessage } from './oauth-popup-message';

export type CalendarPopupConnectResult = 'connected' | 'cancelled' | 'error';

// Only allow known providers to prevent user-controlled input (the redirect's ?provider= query
// param, handled in handleOAuthRedirect) from being used unchecked in URLs. The routing slug for
// outbound calls itself always comes from NativeIntegration.oauthType, not from this list.
// 'google' (the old, pre-google-calendar slug) stays allowlisted alongside it so a user already
// mid-redirect at deploy time isn't left with a silently-dropped connection attempt.
// 'microsoft' (Microsoft Tasks) and 'microsoft-calendar' (Outlook Calendar) are two distinct
// integrations, not two spellings of one - both need their own entry.
const ALLOWED_PROVIDERS = [
  'microsoft',
  'microsoft-calendar',
  'google',
  'google-calendar',
  'asana',
  'clickup',
  'monday',
  'todoist',
]; // extend as needed

/**
 * Replaces { key } tokens in a description string with the corresponding
 * company language value. The key is resolved as a dot-notation path into
 * CustomLanguage (e.g. "todo.item"). When the key resolves to a language
 * section object rather than a string, the section's `item` property is used
 * (e.g. "{ todo }" → language.todo.item).
 */
function interpolateLanguage(description: string, language: CustomLanguage): string {
  if (!description) return '';
  return description.replace(/\{\s*(\S+)\s*\}/g, (match, key: string) => {
    return resolveLanguageKey(key, language) ?? match;
  });
}

/**
 * Resolves a language key from a {@link CustomLanguage} object.
 *
 * The `key` may be a dot-notation path, such as `todo.item`, and each path
 * segment is resolved by traversing the nested properties of `language`.
 *
 * If the resolved value is a string, that string is returned. If the resolved
 * value is an object, this function falls back to returning its `item`
 * property when `item` exists and is a string. If any path segment cannot be
 * resolved, or neither a string value nor a string `item` property is found,
 * the function returns `undefined`.
 *
 * @param key Dot-notation path for the language entry to resolve.
 * @param language The language object to resolve the key against.
 * @returns The resolved language string, the object's `item` string fallback,
 * or `undefined` when no matching string value can be found.
 */
function resolveLanguageKey(key: string, language: CustomLanguage): string | undefined {
  const parts = key.split('.');
  let current: unknown = language;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  if (typeof current === 'string') return current;
  // Key resolved to a section object — fall back to its 'item' sub-property
  if (current != null && typeof current === 'object') {
    const item = (current as Record<string, unknown>)['item'];
    if (typeof item === 'string') return item;
  }
  return undefined;
}

export function normalizeProvider(provider: unknown): string | undefined {
  if (typeof provider !== 'string') return undefined;
  const lower = provider.toLowerCase();
  if (!ALLOWED_PROVIDERS.includes(lower)) return undefined;
  return encodeURIComponent(lower);
}

/** Picks out a calendar integration's oauthType/company - used to choose which provider-specific
 *  CalendarEventsService (Google vs Outlook) to fetch events with. */
export function isOutlookCalendarProvider(integration: Pick<NativeIntegration, 'oauthType' | 'company'>): boolean {
  const provider = (integration.oauthType || integration.company)?.toLowerCase();
  return provider === 'microsoft' || provider === 'microsoft-calendar';
}

/**
 * Provider-agnostic integration connection plumbing: fetch/list catalog integrations, the
 * authorize/exchange OAuth flow (both full-page-redirect and popup variants), and disconnect.
 * Settings' NativeIntegrationService extends this to add the marketplace-specific "pop a success
 * dialog after connecting" behavior, which this class knows nothing about.
 */
@Injectable({
  providedIn: 'root',
})
export class NativeIntegrationCoreService implements NativeIntegrationDisconnectHandler {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly entitlementsService = inject(EntitlementsService);
  protected readonly logger = DatadogLoggerFactory('NativeIntegrationService');
  private readonly document = inject(DOCUMENT);
  readonly nativeIntegrationsUrl = `/api/v4/integrations`;
  private readonly meetingAgentCalendarUrl = `${environment.meetingAgentApiOrigin}/v1/calendar`;

  private readonly _integrations = signal<NativeIntegration[]>([]);
  readonly integrations = this._integrations.asReadonly();

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _language = toSignal(this.store.select(selectLanguage), { requireSync: false });
  private readonly _marketplaceMode3Ff = toSignal(
    this.store.select(selectFeatureFlag(FeatureFlagKeys.webIntegrationMarketplaceMode3)),
    { requireSync: false }
  );
  private readonly _calendarFf = toSignal(
    this.store.select(selectFeatureFlag(FeatureFlagKeys.webIntegrationsCalendar)),
    {
      requireSync: false,
    }
  );

  readonly computedIntegrations = computed<NativeIntegration[]>(() => {
    const integrations = this._integrations();
    if (!Array.isArray(integrations)) return [];
    const language = this._language();
    const ff = this._marketplaceMode3Ff();
    const calendarFf = this._calendarFf();

    // A user may only have one calendar-category integration connected at a time (Google XOR
    // Microsoft, not both) - product wants an explicit disconnect-then-reconnect to switch
    // providers rather than a silent swap. Computed from the raw signal (not computedIntegrations
    // itself) to avoid a circular dependency with calendarIntegrations/activeCalendarIntegration
    // below, which are derived from this computed's own output.
    const activeCalendarIntegrationId = integrations.find(
      i => i.category?.toLowerCase() === 'calendar' && i.userConnections?.some(c => c.status === 'active')
    )?.id;

    return integrations.reduce((acc: NativeIntegration[], integration: NativeIntegration) => {
      // Filter out integrations that we do not want to show in the marketplace
      // Hides Microsoft Tasks from the mode-3 marketplace. Scoped away from the calendar category
      // so Outlook Calendar (same company, oauthType 'microsoft-calendar') still comes through -
      // it has its own gate on the line below.
      if (
        ff &&
        ['microsoft'].includes(integration.company.toLowerCase()) &&
        integration.category?.toLowerCase() !== 'calendar'
      )
        return acc;
      if (!calendarFf && integration.category?.toLowerCase() === 'calendar') return acc;
      // any userConnection with status 'active' should mark the integration as active
      const hasActiveConnection = integration.userConnections?.some(conn => conn.status === 'active') ?? false;
      const isCalendar = integration.category?.toLowerCase() === 'calendar';
      const anotherCalendarIsActive =
        isCalendar && activeCalendarIntegrationId !== undefined && activeCalendarIntegrationId !== integration.id;
      const canConnect = !hasActiveConnection && !anotherCalendarIsActive;
      const integrationStatus = hasActiveConnection ? 'active' : '';
      acc.push({
        ...integration,
        description: language ? interpolateLanguage(integration.description, language) : integration.description,
        canConnect,
        integrationStatus,
      });
      return acc;
    }, []);
  });

  /** Every calendar-category integration available to connect (e.g. Google, Microsoft). */
  readonly calendarIntegrations = computed(() =>
    this.computedIntegrations().filter(integration => integration.category?.toLowerCase() === 'calendar')
  );

  /** The calendar integration with a currently active connection, if any - regardless of provider. */
  readonly activeCalendarIntegration = computed(() =>
    this.calendarIntegrations().find(integration => integration.userConnections?.some(c => c.status === 'active'))
  );

  readonly activeCalendarConnectionId = computed(
    () => this.activeCalendarIntegration()?.userConnections?.find(c => c.status === 'active')?.id
  );

  private readonly _loading = signal(false);
  public readonly loading = this._loading.asReadonly();

  private readonly _error = signal<string | null>(null);
  readonly error = this._error.asReadonly();

  private readonly _exchangeLoading = signal(false);
  readonly exchangeLoading = this._exchangeLoading.asReadonly();

  private readonly _connectLoading = signal(false);
  readonly connectLoading = this._connectLoading.asReadonly();

  private readonly _disconnectLoading = signal(false);
  readonly disconnectLoading = this._disconnectLoading.asReadonly();

  readonly hasIntegrations = computed(() => this.integrations().length > 0);

  fetchNativeIntegrations() {
    this._loading.set(true);
    this._error.set(null);

    // TODO: Should remove this store.select, entitlements are already added by interceptor.
    // See DEV-25952 for details.
    this.store
      .select(selectEntitlements)
      .pipe(
        take(1),
        catchError((err: unknown) => {
          const message = 'Failed to load integrations';
          this.logger.error({ message: 'fetch_native_integrations_entitlements_error', error: err });
          this._error.set(message);
          this._loading.set(false);
          this.store.dispatch(NotificationActions.showErrorToast({ message }));
          return EMPTY;
        }),
        switchMap(entitlements => {
          if (!entitlements) {
            const message = 'Entitlements not found';
            this._error.set(message);
            this._loading.set(false);
            this.store.dispatch(NotificationActions.showErrorToast({ message }));
            return EMPTY;
          }
          return this.http
            .get<NativeIntegrationsResponse>(`${this.nativeIntegrationsUrl}`, {
              responseType: 'json',
            })
            .pipe(
              tap({
                next: integrations => {
                  this._integrations.set(integrations.integrations);
                },
                error: (err: unknown) => {
                  const message = 'Failed to load integrations';
                  this.logger.error({ message: 'fetch_native_integrations_error', error: err });
                  this._error.set(message);
                  this.store.dispatch(NotificationActions.showErrorToast({ message }));
                },
              }),
              catchError(() => EMPTY)
            );
        })
      )
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this._loading.set(false))
      )
      .subscribe();
  }

  getLoginUrl(oauthType: string, integrationType: string, redirectUri: string): void {
    this._connectLoading.set(true);
    this.store.dispatch(SpinnerActions.startPrimary({ source: 'NativeIntegrationService.getLoginUrl' }));

    this.getAuthorizeUrl$(oauthType, integrationType, redirectUri)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          this._connectLoading.set(false);
          this.store.dispatch(SpinnerActions.stopPrimary({ source: 'NativeIntegrationService.getLoginUrl' }));
        })
      )
      .subscribe(({ redirectUrl }) => {
        this.document.location.href = redirectUrl;
      });
  }

  /**
   * Shared authorize-URL lookup behind both the full-page-redirect flow (`getLoginUrl`) and the
   * popup flow (`connectCalendarViaPopup`). Returns `redirectUriUsed` (the exact redirectUri sent
   * to the authorize call) alongside `redirectUrl` because the later token exchange must be called
   * with that same redirectUri, and the popup flow can't rederive it from `document.location` the
   * way the redirect flow does (the popup, not the caller's page, is what the provider redirects).
   * Swallows failures to EMPTY (toast already dispatched) to match the pre-existing getLoginUrl
   * behavior.
   */
  private getAuthorizeUrl$(
    oauthType: string,
    integrationType: string,
    redirectUri: string
  ): Observable<{ redirectUrl: string; redirectUriUsed: string }> {
    if (!redirectUri) {
      redirectUri = this.redirectBaseUri;
    }
    const normalizedProvider = normalizeProvider(oauthType);
    if (!normalizedProvider) {
      const message = 'Invalid provider';
      this._error.set(message);
      this.store.dispatch(NotificationActions.showErrorToast({ message }));
      return EMPTY;
    }
    oauthType = normalizedProvider;
    integrationType = integrationType.toLowerCase();
    this._error.set(null);
    const redirectUriUsed = redirectUri.includes('?')
      ? `${redirectUri}&provider=${oauthType}`
      : `${redirectUri}?provider=${oauthType}`;

    return this.entitlementsService.getOrFetchEntitlementsToken().pipe(
      take(1),
      catchError(() => EMPTY),
      switchMap(entitlementsToken => {
        if (!entitlementsToken) {
          const message = 'Entitlements not found';
          this._error.set(message);
          this.store.dispatch(NotificationActions.showErrorToast({ message }));
          return EMPTY;
        }
        const headers = {
          Entitlements: entitlementsToken,
        };

        return this.http
          .post<{
            redirectUrl: string;
          }>(
            `${this.nativeIntegrationsUrl}/oauth/${oauthType}/authorize`,
            {
              redirectUri: redirectUriUsed,
              integrationProduct: integrationType,
            },
            { headers }
          )
          .pipe(
            tap({
              error: (err: unknown) => {
                const message = 'Failed to get login URL';
                this.logger.error({ message: 'get_login_url_error', error: err });
                this._error.set(message);
                this.store.dispatch(NotificationActions.showErrorToast({ message }));
              },
            }),
            map(result => ({ redirectUrl: result.redirectUrl, redirectUriUsed })),
            catchError(() => EMPTY)
          );
      })
    );
  }

  exchangeAuthCode(provider: string, code: string, state: string = 'default'): void {
    const currentUrl = this.document.location.href.split('?')[0];
    const normalizedProvider = normalizeProvider(provider.toLowerCase());
    const redirectUri = normalizedProvider ? `${currentUrl}?provider=${normalizedProvider}` : currentUrl;

    this._exchangeLoading.set(true);
    this.store.dispatch(SpinnerActions.startPrimary({ source: 'NativeIntegrationService.exchangeAuthCode' }));

    this.performExchange$(provider, code, state, redirectUri)
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          this._exchangeLoading.set(false);
          this.store.dispatch(SpinnerActions.stopPrimary({ source: 'NativeIntegrationService.exchangeAuthCode' }));
        })
      )
      .subscribe();
  }

  /**
   * Shared exchange call behind both `exchangeAuthCode` (fire-and-forget, swallows errors to EMPTY
   * after toasting) and `connectCalendarViaPopup` (needs the error to actually propagate so it can
   * resolve its result observable rather than hang). `redirectUri` must be the exact value sent to
   * the matching authorize call - it is NOT rederived from `document.location` here, since for the
   * popup flow that would be the caller's page, not the popup's callback URL the provider actually
   * redirected to.
   */
  private performExchange$(provider: string, code: string, state: string, redirectUri: string): Observable<void> {
    const normalizedProvider = normalizeProvider(provider.toLowerCase());
    if (!normalizedProvider) {
      const message = 'Invalid provider';
      this._error.set(message);
      this.store.dispatch(NotificationActions.showErrorToast({ message }));
      return EMPTY;
    }
    provider = normalizedProvider;
    const body: Record<string, string> = {
      authCode: code,
      redirectUri,
      state,
    };

    return this.entitlementsService.getOrFetchEntitlementsToken().pipe(
      take(1),
      catchError(() => EMPTY),
      switchMap(entitlementsToken => {
        if (!entitlementsToken) {
          const message = 'Entitlements not found';
          this._error.set(message);
          this.store.dispatch(NotificationActions.showErrorToast({ message }));
          return EMPTY;
        }
        const headers = { Entitlements: entitlementsToken };
        return this.http.post(`${this.nativeIntegrationsUrl}/oauth/${provider}/exchange`, body, { headers }).pipe(
          tap({
            next: () => {
              this.fetchNativeIntegrations();
            },
            error: (err: unknown) => {
              const message = 'Failed to connect integration';
              this.logger.error({ message: 'exchange_auth_code_error', error: err });
              this._error.set(message);
              this.store.dispatch(NotificationActions.showErrorToast({ message }));
            },
          }),
          map(() => undefined)
        );
      })
    );
  }

  /**
   * Popup-window variant of the connect flow, used by callers (e.g. the meeting-scheduling
   * calendar picker) that can't afford a full-page redirect. Never calls `setPendingProvider` on
   * the settings marketplace subclass, so unlike the redirect flow it does not pop the generic
   * post-connect success wizard - callers decide their own success UI from the returned result
   * instead.
   */
  connectCalendarViaPopup(integration: NativeIntegration): Observable<CalendarPopupConnectResult> {
    const oauthType = integration.oauthType || integration.company;

    // Must open synchronously in the caller's click handler, before any async work, or popup
    // blockers will silently drop it.
    const popup = window.open('', 'ninety-oauth', 'width=520,height=650');
    if (!popup) {
      this.store.dispatch(
        NotificationActions.showErrorToast({ message: 'Please allow popups to connect your calendar' })
      );
      return of<CalendarPopupConnectResult>('error');
    }

    const redirectUri = `${this.document.location.origin}/oauth-popup-callback`;

    return this.getAuthorizeUrl$(oauthType, integration.product, redirectUri).pipe(
      switchMap(({ redirectUrl, redirectUriUsed }) => {
        popup.location.href = redirectUrl;
        return this.waitForPopupResult$(popup, redirectUriUsed);
      }),
      defaultIfEmpty('error' as const),
      tap(result => {
        if (result === 'connected') {
          this.store.dispatch(
            NotificationActions.showSuccessToast({ message: `Connected to ${integration.displayName}` })
          );
        } else {
          popup.close();
        }
      })
    );
  }

  // Once the popup navigates to the provider's (cross-origin) page, that page's
  // Cross-Origin-Opener-Policy can make `popup.closed` read `true` from the opener - immediately
  // and permanently - even though the window is still genuinely open (confirmed against
  // accounts.google.com: no exception, just a stable false reading). So `.closed` can't be used to
  // detect a manually-closed popup during the OAuth leg at all; this timeout is the fallback for an
  // abandoned popup instead.
  private static readonly POPUP_RESULT_TIMEOUT_MS = 5 * 60 * 1000;

  private waitForPopupResult$(popup: Window, redirectUriUsed: string): Observable<CalendarPopupConnectResult> {
    return new Observable<CalendarPopupConnectResult>(subscriber => {
      const onMessage = (event: MessageEvent<OAuthPopupMessage>) => {
        if (event.origin !== this.document.location.origin || event.data?.source !== OAUTH_POPUP_MESSAGE_SOURCE) {
          return;
        }
        if ('error' in event.data) {
          // Provider denied consent or the redirect was missing a code - treat as a cancel, not a
          // toast-worthy error, since it's most likely the user backing out.
          subscriber.next('cancelled');
          subscriber.complete();
          return;
        }
        const { provider, code, state } = event.data;
        this.performExchange$(provider, code, state ?? 'default', redirectUriUsed).subscribe({
          next: () => {
            subscriber.next('connected');
            subscriber.complete();
          },
          error: () => {
            subscriber.next('error');
            subscriber.complete();
          },
        });
      };

      const timeoutId = window.setTimeout(() => {
        subscriber.next('cancelled');
        subscriber.complete();
      }, NativeIntegrationCoreService.POPUP_RESULT_TIMEOUT_MS);

      window.addEventListener('message', onMessage);

      return () => {
        window.removeEventListener('message', onMessage);
        window.clearTimeout(timeoutId);
      };
    });
  }

  handleOAuthRedirect(params: Params): void {
    const { provider, code, state } = params;

    const normalizedProvider = normalizeProvider(provider);
    const hasValidCode = typeof code === 'string' && code.length > 0;
    const hasValidState = typeof state === 'undefined' || (typeof state === 'string' && state.length > 0);

    if (normalizedProvider && hasValidCode && hasValidState) {
      this.logger.info({ message: 'handle_oauth_redirect', data: { provider: normalizedProvider } });
      this.exchangeAuthCode(normalizedProvider, code, state);
    } else {
      this.logger.warn({
        message: 'handle_oauth_redirect_invalid',
        data: { provider: normalizedProvider, hasValidCode, hasValidState },
      });
    }
  }

  handleOAuthRedirectAndCleanUrl(params: Params): void {
    this.handleOAuthRedirect(params);
    this.document.location.replace(this.cleanRedirectUrl(this.document.location.href));
  }

  /** `integration` is optional only for backwards compatibility with existing callers - pass it
   *  whenever available so a disconnected calendar integration also tears down its Recall
   *  calendar (meeting-agent otherwise has no way to learn the connection was removed here, and
   *  would keep reusing the stale one against reconnection with a different provider). */
  disconnectIntegration(userConnectionId: string, integration?: NativeIntegration): void {
    this._disconnectLoading.set(true);
    this.store.dispatch(SpinnerActions.startPrimary({ source: 'NativeIntegrationService.disconnectIntegration' }));

    this.entitlementsService
      .getOrFetchEntitlementsToken()
      .pipe(
        take(1),
        catchError(() => EMPTY),
        switchMap(entitlementsToken => {
          if (!entitlementsToken) {
            const message = 'Entitlements not found';
            this._error.set(message);
            this._disconnectLoading.set(false);
            this.store.dispatch(NotificationActions.showErrorToast({ message }));
            return EMPTY;
          }
          const headers = { Entitlements: entitlementsToken };
          return this.http.delete(`${this.nativeIntegrationsUrl}/oauth/${userConnectionId}`, { headers }).pipe(
            tap({
              next: () => {
                this.fetchNativeIntegrations();
                if (integration?.category?.toLowerCase() === 'calendar') {
                  this.disconnectRecallCalendar(integration);
                }
              },
              error: (err: unknown) => {
                const message = 'Failed to delete integration';
                this.logger.error({ message: 'delete_integration_error', error: err });
                this.store.dispatch(NotificationActions.showErrorToast({ message }));
              },
            }),
            catchError(() => EMPTY)
          );
        }),
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          this._disconnectLoading.set(false);
          this.store.dispatch(SpinnerActions.stopPrimary({ source: 'NativeIntegrationService.disconnectIntegration' }));
        })
      )
      .subscribe();
  }

  // Best-effort, fire-and-forget: mirrors RecallCalendarService.unscheduleBot's pattern in
  // web-pages/meeting - meeting-agent no-ops harmlessly if there's nothing to disconnect, and the
  // ninety-side disconnect above has already succeeded regardless of whether this one does.
  // Swallows errors rather than surfacing a second toast for a cleanup step the user didn't
  // directly ask for.
  private disconnectRecallCalendar(integration: NativeIntegration): void {
    const calendarProvider = isOutlookCalendarProvider(integration) ? 'microsoft-outlook' : 'google-calendar';
    this.http
      .delete<void>(this.meetingAgentCalendarUrl, { params: { calendarProvider } })
      .pipe(
        catchError((err: unknown) => {
          this.logger.error({ message: 'disconnect_recall_calendar_error', error: err });
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  connectIntegration(integrationId: string) {
    this.logger.info({ message: 'connect_integration', data: { integrationId } });
    this.fetchNativeIntegrations();
  }

  get redirectBaseUri(): string {
    return this.document.location.origin + this.cleanRedirectUrl(this.document.location.pathname);
  }

  cleanRedirectUrl(url: string): string {
    const pathname = url.split(/[?#]/, 1)[0];
    // Longer slugs come first in the alternation: a bare `microsoft` branch that matched first
    // would leave `-calendar` behind on a /microsoft-calendar path.
    return pathname.replace(/\/(google-calendar|google|microsoft-calendar|microsoft|marketplace|zapier)(\/.*)?$/, '');
  }
}
